import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';
import _image_service from "../../../../../../services/ImageService";

interface EnlightenedHistoryPageProps {
}

const EnlightenedHistoryPage = (props: EnlightenedHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_post_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Enlightened Age - Chase the Fox`,
        description: "The Enlightened Age of Casarok was the time of steam and clockwork power in the world of Casarok!",
        keywords: ["casarok", "casarok history", "enlightened age", "fantasy timeline", "fantasy world history", "fantasy steampunk era"],
        imgSrc: _map_src,
        url: `world/history/enlightened-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Post-Cataclysm Map'
                         alt="A map of Casarok after the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Enlightened Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_post_cataclysm.png`)}
                             title='Casarok Post-Cataclysm Map'
                             alt="A map of Casarok after the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years 1500 - 1700 ACE.</em>
                    </p>
                    <p>
                        The Enlightened Age saw a huge culture shift with the presence of steam and clockwork power
                        becoming a phenomenon the world over. These technologies completely dominated the world at the
                        time. The peoples of Casarok wanted to dress like engineers, own the latest technology, and
                        some even wanted to tinker themselves. It was also trendy for some to change their family names
                        to show their love for this new technology, which is why family lineages like
                        the <NavLink className="world-link" to="/characters/malichi">Mechanaruses</NavLink> and
                        the <NavLink className="world-link" to="/characters/wally">Cognisarians</NavLink>. Steam and
                        clockwork power also formed a heated rivalry, which came to a head in the later part of this
                        age.
                    </p>
                    <p>
                        Air travel was first established during this time as well. Zeppelins and airships ruled the
                        skies and made travel over land and across the oceans much more pleasant for the general
                        anthros of the world, especially those who could not fly themselves. It became a big business
                        opportunity, and some air travel companies that originated here still exist in the modern
                        day. Trade by air was also becoming feasible as it was faster than ferrying cargo by sea despite
                        its more expensive nature. The race to take over the skies also became a hot button issue as
                        new laws and regulations needed to be passed to safeguard flying anthros and cloudlands.
                    </p>
                    <p>
                        <em>How does Casarok's Enlightened Age differ from the Age of Enlightenment of Earth?</em><br/>
                        Casarok had more advanced technology. Advanced steam and clockwork powered devices were only the
                        fantasy on Earth. <NavLink className="world-link" to='/world/magic/items/bulletguard'>Bulletguards</NavLink> also
                        continued to keep advancements on firearms in check and slow them down considerably.
                    </p>
                    <h3 className="headline">Notable Events</h3>
                    <ul className="spaced">
                        <li>
                            <em>1506 ACE</em>: The powerful advanced magics of Sequea spread throughout the world.
                        </li>
                        <li>
                            <em>1522 ACE</em>: The first voyage across an ocean is made in an early airship from
                            Erthuria to Terasama.
                        </li>
                        <li>
                            <em>1527 ACE</em>: Greater and Lower Tilland merge to reform the nation of Tilland.
                        </li>
                        <li>
                            <em>1530 ACE</em>: The rate at which steam and clockwork powered inventions are patented
                            explodes.
                        </li>
                        <li>
                            <em>1534 ACE</em>: The rate at which steam and clockwork powered inventions are patented
                            explodes.
                        </li>
                        <li>
                            <em>1540 ACE</em>: More ranged weaponry from bows to firearms make use of clockwork
                            mechanics.
                        </li>
                        <li>
                            <em>1551 ACE</em>: Commercial zeppelin and airship travel becomes available to the public.
                        </li>
                        <li>
                            <em>1567 ACE</em>: The nation of Hermonia separates from Waleya.
                        </li>
                        <li>
                            <em>1572 ACE</em>: A revolution occurs on the border of Bogarta and Espachino, creating the
                            nation of Roniday out of territory from the two nations.
                        </li>
                        <li>
                            <em>1588 ACE</em>: The nation of Otala secedes from Temaris.
                        </li>
                        <li>
                            <em>1612 ACE</em>: Steam and clockwork powered mechs are invented.
                        </li>
                        <li>
                            <em>1615 ACE</em>: The nation of Lakojia splits, forming Manulia.
                        </li>
                        <li>
                            <em>1623 ACE</em>: Rifling is invented, again pushing firearms
                            past <NavLink className="world-link" to='/world/magic/items/bulletguard'>bulletguards</NavLink> in
                            the ongoing arms race.
                        </li>
                        <li>
                            <em>1630 ACE</em>: The nation of Bovas revolts and breaks away from Goldhia.
                        </li>
                        <li>
                            <em>1644 ACE</em>: Primitive steam and clockwork automatons are first created.
                        </li>
                        <li>
                            <em>1661 ACE</em>: Bulletguards capable of protection from rifles are now widely available
                            to the public.
                        </li>
                        <li>
                            <em>1683 ACE</em>: Battles break out between the creators of steam and clockwork machines
                            and their supporters.
                        </li>
                        <li>
                            <em>1686 ACE</em>: Conflict ends between the supporters of the two competing technologies.
                            Support for these technologies begins to wane among the public.
                        </li>
                        <li>
                            <em>1691 ACE</em>: Sustainable, non-magical electricity is invented in the wake of the
                            steam and clockwork power collapse.
                        </li>
                        <li>
                            <em>1697 ACE</em>: The first refined oil leads the way to further fossil fuel development.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default EnlightenedHistoryPage;